import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { ANIMATED_BACKGROUNDS } from '../SettingsModal';

const { FiCopy, FiCheck, FiZap, FiBookmark, FiFolder, FiSearch, FiShare2 } = FiIcons;

function pickFirst(...vals) {
  for (const v of vals) {
    if (v !== undefined && v !== null && String(v).trim() !== '') return v;
  }
  return undefined;
}

function cap(s) {
  if (!s || typeof s !== 'string') return s;
  const t = s.trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : t;
}

function norm(s) {
  return (s || '').toString().trim().toLowerCase();
}

function findFolderByAnyRef(data, ref) {
  if (!ref) return null;
  const r = norm(ref);

  return (
    data[ref] ||
    Object.values(data).find(
      (f) =>
        norm(f.id) === r ||
        norm(f._dbId) === r ||
        norm(f._realFolderId) === r ||
        norm(f.title) === r
    ) ||
    Object.values(data).find(
      (f) => norm(f.title).includes(r) || r.includes(norm(f.title))
    ) ||
    null
  );
}

function findBookmarkByAnyRef(data, ref) {
  if (!ref) return null;
  const r = norm(ref);
  const all = Object.values(data).flatMap((f) =>
    (f.items || []).filter((i) => i.type === 'link')
  );

  return (
    all.find(
      (b) =>
        norm(b.id) === r ||
        norm(b._realBookmarkId) === r ||
        norm(b.title) === r ||
        norm(b.url) === r
    ) ||
    all.find((b) => norm(b.title).includes(r) || norm(b.url).includes(r)) ||
    null
  );
}

function renderInline(text, tc) {
  const parts = [];
  const regex = /(\*\*(.+?)\*\*|\*(.+?)\*|`([^`]+)`|(https?:\/\/[^\s]+))/g;
  let last = 0;
  let m;

  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) parts.push({ type: 'text', value: text.slice(last, m.index) });
    if (m[2] !== undefined) parts.push({ type: 'bold', value: m[2] });
    else if (m[3] !== undefined) parts.push({ type: 'italic', value: m[3] });
    else if (m[4] !== undefined) parts.push({ type: 'code', value: m[4] });
    else if (m[5] !== undefined) parts.push({ type: 'url', value: m[5] });
    last = m.index + m[0].length;
  }

  if (last < text.length) parts.push({ type: 'text', value: text.slice(last) });

  return parts.map((p, i) => {
    if (p.type === 'bold') return <strong key={i} style={{ color: tc.text, fontWeight: 700 }}>{p.value}</strong>;
    if (p.type === 'italic') return <em key={i} style={{ opacity: 0.85 }}>{p.value}</em>;
    if (p.type === 'code') {
      return (
        <code key={i} className="text-xs px-1.5 py-0.5 rounded font-mono" style={{ backgroundColor: `${tc.accent}20`, color: tc.accent }}>
          {p.value}
        </code>
      );
    }
    if (p.type === 'url') {
      return (
        <a key={i} href={p.value} target="_blank" rel="noreferrer" className="underline break-all" style={{ color: tc.accent }}>
          {p.value}
        </a>
      );
    }
    return <span key={i}>{p.value}</span>;
  });
}

export function CopyButton({ text, tc }) {
  const [copied, setCopied] = useState(false);

  return (
    <button
      onClick={() =>
        navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1800);
        })
      }
      className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full transition-all flex-shrink-0"
      style={{ backgroundColor: `${tc.accent}22`, color: tc.accent, border: `1px solid ${tc.accent}44` }}
    >
      <SafeIcon icon={copied ? FiCheck : FiCopy} className="w-2.5 h-2.5" />
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function parseCredLine(txt, inCredCtx) {
  if (txt.includes(':') && !txt.startsWith('http')) {
    const ci = txt.indexOf(':');
    const lbl = txt.slice(0, ci + 1).trim();
    const val = txt.slice(ci + 1).trim();
    if (val.length > 0) return { lbl, val, isStrong: false };
  }

  const firstWord = txt.split(' ')[0];
  const isEmail = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}/.test(firstWord);
  const hasLoginKw = /google login|apple login|login with/i.test(txt);

  if (isEmail || hasLoginKw) {
    return { lbl: null, val: txt, isStrong: true };
  }

  if (inCredCtx && !txt.includes(' ') && txt.length > 4 && !txt.toLowerCase().includes('here')) {
    return { lbl: null, val: txt, isStrong: false };
  }

  return null;
}

function isCredentialContext(lines, idx) {
  for (let j = Math.max(0, idx - 5); j <= idx; j++) {
    if (/credential|password|login|username|token|key|secret|email/i.test(lines[j])) return true;
  }
  return false;
}

export function MessageContent({ content, tc }) {
  const lines = content.split('\n');
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const trimmed = lines[i].trim();

    if (!trimmed) {
      elements.push(<div key={`sp-${i}`} className="h-1" />);
      i++;
      continue;
    }

    if (/^#{1,3}\s/.test(trimmed)) {
      const txt = trimmed.replace(/^#{1,3}\s/, '');
      elements.push(
        <p key={i} className="text-xs font-bold uppercase tracking-wide mt-2 mb-0.5" style={{ color: tc.accent }}>
          {renderInline(txt, tc)}
        </p>
      );
      i++;
      continue;
    }

    if (/^\d+\.\s/.test(trimmed)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i].trim())) {
        const num = lines[i].trim().match(/^(\d+)\.\s/)[1];
        const txt = lines[i].trim().replace(/^\d+\.\s/, '');
        items.push(
          <div key={i} className="flex items-start gap-2 mb-1">
            <span className="flex-shrink-0 w-4 h-4 rounded-full text-[9px] flex items-center justify-center font-bold mt-0.5" style={{ backgroundColor: `${tc.accent}25`, color: tc.accent }}>
              {num}
            </span>
            <span className="text-sm leading-relaxed flex-1">{renderInline(txt, tc)}</span>
          </div>
        );
        i++;
      }
      elements.push(<div key={`nl-${i}`} className="my-1">{items}</div>);
      continue;
    }

    if (/^[-•*]\s/.test(trimmed)) {
      const items = [];
      while (i < lines.length && /^[-•*]\s/.test(lines[i].trim())) {
        const txt = lines[i].trim().replace(/^[-•*]\s/, '');
        const inCredCtx = isCredentialContext(lines, i);
        const credParsed = parseCredLine(txt, inCredCtx);
        const isCred = credParsed && (inCredCtx || credParsed.isStrong);

        if (isCred) {
          const { lbl, val } = credParsed;
          items.push(
            <div key={i} className="flex items-start gap-2 mb-1.5">
              <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full mt-2" style={{ backgroundColor: tc.accent }} />
              <span className="text-sm leading-relaxed flex-1 flex items-center flex-wrap gap-1.5">
                {lbl && <span style={{ opacity: 0.7 }}>{lbl}</span>}
                <code className="font-mono text-xs px-1.5 py-0.5 rounded break-all" style={{ backgroundColor: `${tc.accent}18`, color: tc.accent }}>
                  {val}
                </code>
                <CopyButton text={val} tc={tc} />
              </span>
            </div>
          );
        } else {
          items.push(
            <div key={i} className="flex items-start gap-2 mb-1">
              <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full mt-2" style={{ backgroundColor: tc.accent }} />
              <span className="text-sm leading-relaxed flex-1">{renderInline(txt, tc)}</span>
            </div>
          );
        }
        i++;
      }
      elements.push(<div key={`bl-${i}`} className="my-1">{items}</div>);
      continue;
    }

    const inCredCtx = isCredentialContext(lines, i);
    const credParsed = parseCredLine(trimmed, inCredCtx);
    const isCred = credParsed && (inCredCtx || credParsed.isStrong);

    if (isCred) {
      const { lbl, val } = credParsed;
      elements.push(
        <div key={i} className="flex items-center flex-wrap gap-1.5 mb-1.5">
          {lbl && <span className="text-sm" style={{ opacity: 0.7 }}>{lbl}</span>}
          <code className="font-mono text-xs px-1.5 py-0.5 rounded break-all" style={{ backgroundColor: `${tc.accent}18`, color: tc.accent }}>
            {val}
          </code>
          <CopyButton text={val} tc={tc} />
        </div>
      );
      i++;
      continue;
    }

    elements.push(<p key={i} className="text-sm leading-relaxed mb-1">{renderInline(trimmed, tc)}</p>);
    i++;
  }

  return <div className="space-y-0.5">{elements}</div>;
}

export const TypingDots = ({ color }) => (
  <div className="flex items-center gap-1 py-1">
    {[0, 1, 2].map((i) => (
      <motion.div
        key={i}
        className="w-1.5 h-1.5 rounded-full"
        style={{ backgroundColor: color }}
        animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1.1, 0.8] }}
        transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2 }}
      />
    ))}
  </div>
);

export const Message = ({ msg, tc }) => {
  const isUser = msg.role === 'user';
  const isSystem = msg.role === 'system';

  const userBg = tc.chatUserBg || tc.accent;
  const userTextColor = tc.chatUser || '#000000';
  const aiBgColor = tc.chatAiBg || tc.card;
  const aiTextColor = tc.chatAi || tc.text || '#F5F5F5';
  const aiBg = tc.aiButtonBg || tc.accent;
  const aiText = tc.aiButtonText || '#FFFFFF';

  if (isSystem) {
    return (
      <div className="flex justify-center my-1">
        <span className="text-[10px] px-3 py-1 rounded-full text-center" style={{ color: tc.muted, backgroundColor: `${tc.accent}18` }}>
          {msg.content.split('\n').map((line, i) => <div key={i}>{line}</div>)}
        </span>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
      {!isUser && (
        <div className="w-6 h-6 rounded-full flex items-center justify-center mr-2 mt-0.5 flex-shrink-0" style={{ backgroundColor: aiBg }}>
          <SafeIcon icon={FiZap} className="w-3 h-3" style={{ color: aiText }} />
        </div>
      )}

      <div
        className="max-w-[82%] px-3.5 py-2.5"
        style={
          isUser
            ? {
                backgroundColor: userBg,
                color: userTextColor,
                borderRadius: '18px 18px 4px 18px',
                fontSize: '14px',
                lineHeight: '1.5',
              }
            : {
                backgroundColor: aiBgColor,
                color: aiTextColor,
                border: `1px solid ${tc.border}`,
                borderRadius: '18px 18px 18px 4px',
              }
        }
      >
        {msg.typing ? (
          <TypingDots color={tc.accent} />
        ) : isUser ? (
          <span className="text-sm" style={{ color: userTextColor }}>{msg.content}</span>
        ) : (
          <MessageContent content={msg.content} tc={{ ...tc, text: aiTextColor }} />
        )}
      </div>
    </motion.div>
  );
};

function normalizeUrlString(url) {
  try {
    const u = new URL(url);
    return (u.origin + u.pathname + u.search + u.hash).toLowerCase().replace(/\/+$/, '');
  } catch (_) {
    return (url || '').trim().toLowerCase().replace(/\/+$/, '');
  }
}

const ACTION_LABELS = {
  open_bookmark: (a, data) => {
    // 1. If explicit title is provided and not a placeholder, prefer it!
    if (a.title && norm(a.title) !== 'link' && norm(a.title) !== 'bookmark') {
      return `🔗 Opened "${cap(a.title)}"`;
    }
    // 2. If a specific bookmark matches the exact URL, use its title
    if (a.url) {
      const all = Object.values(data || {}).flatMap((f) => (f.items || []).filter((i) => i.type === 'link'));
      const targetNorm = normalizeUrlString(a.url);
      const exact = all.find((b) => b.url && normalizeUrlString(b.url) === targetNorm);
      if (exact && exact.title) return `🔗 Opened "${cap(exact.title)}"`;
    }
    // 3. If a specific bookmark ID is present, verify its URL matches action.url before using its title
    if (a.id || a.bookmarkId || a.bmId) {
      const all = Object.values(data || {}).flatMap((f) => (f.items || []).filter((i) => i.type === 'link'));
      const idToFind = pickFirst(a.id, a.bookmarkId, a.bmId);
      const bm = all.find((b) => b.id === idToFind || b._realBookmarkId === idToFind);
      if (bm && (!a.url || normalizeUrlString(bm.url) === normalizeUrlString(a.url))) {
        return `🔗 Opened "${cap(bm.title)}"`;
      }
    }
    // 4. Fallback to clean domain name from URL
    if (a.url) {
      try {
        const hostname = new URL(a.url).hostname.replace(/^www\./, '');
        const name = hostname.split('.')[0] || hostname;
        return `🔗 Opened "${cap(name)}"`;
      } catch (_) {}
    }
    return '🔗 Opened link';
  },

  get_credentials: (a, data) => {
    const bm = findBookmarkByAnyRef(data, pickFirst(a.id, a.bookmarkId, a.bmId, a.title, a.name, a.query));
    return bm?.credentials?.trim()
      ? `🔒 Credentials for "${cap(bm.title)}"`
      : `🔒 No credentials for "${cap(bm?.title || a.title || 'Bookmark')}"`;
  },

  get_note: (a, data) => {
    const bm = findBookmarkByAnyRef(data, pickFirst(a.id, a.bookmarkId, a.bmId, a.title, a.name, a.query));
    return bm?.note?.trim()
      ? `📝 Note for "${cap(bm.title)}"`
      : `📝 No note for "${cap(bm?.title || a.title || 'Bookmark')}"`;
  },

  add_bookmark: (a) => `➕ Added "${cap(pickFirst(a.title, a.name) || 'Bookmark')}"${a.credentials ? ' 🔒' : ''}${a.note ? ' 📝' : ''}`,

  add_folder: (a, data) => {
    const title = cap(pickFirst(a.title, a.folderTitle, a.name) || 'Folder');
    const parentRef = pickFirst(a.parentId, a.parentTitle, a.targetParentId, a.targetParentTitle);
    const parentFolder = parentRef && parentRef !== 'root' ? findFolderByAnyRef(data, parentRef) : null;
    if (parentRef && parentRef !== 'root') {
      return `📁 Created folder "${title}" in "${cap(parentFolder?.title || 'Subfolder')}"`;
    }
    return `📁 Created folder "${title}" in homepage`;
  },

  share_folder: (a) => `🔗 Shared folder with ${a.inviteeEmail || a.email || 'member'} as ${a.permission || 'viewer'}`,
  revoke_shared_member: (a) => `🚫 Removed ${a.inviteeEmail || a.email || 'member'} from folder`,
  update_permission: (a) => `🔄 Changed ${a.inviteeEmail || a.email || 'member'} to ${a.permission || 'viewer'}`,
  leave_shared_folder: () => '👋 Left shared folder',

  delete_bookmark: (a, data) => {
    const bm = findBookmarkByAnyRef(data, pickFirst(a.id, a.bookmarkId, a.bmId, a.title, a.name, a.url));
    const title = cap(bm?.title || pickFirst(a.title, a.name) || 'Bookmark');
    return `🗑️ Deleted "${title}"`;
  },

  delete_folder: (a, data) => {
    const folder = findFolderByAnyRef(data, pickFirst(a.id, a.folderId, a.title, a.folderTitle, a.name));
    const title = cap(folder?.title || pickFirst(a.title, a.folderTitle, a.name) || 'Folder');
    return `🗑️ Deleted folder "${title}"`;
  },

  bulk_delete: (a) => `🗑️ Deleted ${a.ids?.length || 0} items`,
  rename_bookmark: (a) => `✏️ Renamed to "${cap(pickFirst(a.title, a.newTitle, a.toTitle, a.name) || 'Bookmark')}"`,
  rename_folder: (a) => `✏️ Renamed to "${cap(pickFirst(a.title, a.newTitle, a.toTitle, a.name) || 'Folder')}"`,

  move_bookmark: (a, data) => {
    const bm = findBookmarkByAnyRef(data, pickFirst(a.id, a.bookmarkId, a.bmId, a.title, a.name));
    let dest = pickFirst(a.targetFolderTitle, a.targetFolderId);
    if (a.targetFolderId) {
      const folder = findFolderByAnyRef(data, a.targetFolderId);
      if (folder?.title) dest = folder.title;
    }
    return `📦 Moved "${cap(bm?.title || 'Bookmark')}" → ${cap(dest || 'Folder')}`;
  },

  move_folder: (a, data) => {
    const src = findFolderByAnyRef(data, pickFirst(a.folderId, a.folderTitle, a.title, a.name));
    let destName = a.targetParentTitle || (a.targetParentId === 'root' ? 'Home' : null);
    if (!destName && a.targetParentId) {
      const f = findFolderByAnyRef(data, a.targetParentId);
      destName = f?.title || 'Home';
    }
    return `📁 Moved folder "${cap(src?.title || a.folderTitle || 'Folder')}" → ${cap(destName || 'Home')}`;
  },

  update_note: (a, data) => {
    const bm = findBookmarkByAnyRef(data, pickFirst(a.id, a.bookmarkId, a.bmId, a.title, a.name));
    return `📝 Note updated for "${cap(bm?.title || 'Bookmark')}"`;
  },

  update_credentials: (a, data) => {
    const bm = findBookmarkByAnyRef(data, pickFirst(a.id, a.bookmarkId, a.bmId, a.title, a.name));
    return `🔒 Credentials updated for "${cap(bm?.title || 'Bookmark')}"`;
  },

  update_url: (a, data) => {
    const bm = findBookmarkByAnyRef(data, pickFirst(a.id, a.bookmarkId, a.bmId, a.title, a.name));
    return `🔗 URL updated for "${cap(bm?.title || 'Bookmark')}"`;
  },

  update_bookmark: (a, data) => {
    const bm = findBookmarkByAnyRef(data, pickFirst(a.id, a.bookmarkId, a.bmId, a.title, a.name));
    return `✏️ Updated "${cap(bm?.title || 'Bookmark')}"`;
  },

  apply_theme: (a) => `🎨 Accent → ${a.customAccent || a.accent || 'updated'}`,

  apply_background: (a) =>
    a.customUrl
      ? '🖼️ Custom background applied'
      : `🌌 Background → ${ANIMATED_BACKGROUNDS.find((b) => b.id === a.backgroundType)?.name || a.backgroundType || 'updated'}`,

  apply_mode: (a) => (a.mode === 'dark' ? '🌙 Switched to Dark Mode' : '☀️ Switched to Light Mode'),

  apply_font_colors: (a) => {
    const parts = [];
    if (a.accent) parts.push(`accent: ${a.accent}`);
    if (a.heading) parts.push(`heading: ${a.heading}`);
    if (a.text || a.body) parts.push(`body: ${a.text || a.body}`);
    if (a.muted || a.subtitle) parts.push(`subtitle: ${a.muted || a.subtitle}`);
    if (a.label || a.labels) parts.push(`labels: ${a.label || a.labels}`);
    if (a.chatUserBg) parts.push(`chat bg (you): ${a.chatUserBg}`);
    if (a.chatUser) parts.push(`chat text (you): ${a.chatUser}`);
    if (a.chatAiBg) parts.push(`chat bg (AI): ${a.chatAiBg}`);
    if (a.chatAi) parts.push(`chat text (AI): ${a.chatAi}`);
    if (a.aiButtonBg) parts.push(`ai base: ${a.aiButtonBg}`);
    if (a.aiButtonText || a.aiIconColor) parts.push(`ai icon: ${a.aiButtonText || a.aiIconColor}`);
    if (a.pageBg || a.backgroundColor) parts.push(`page bg: ${a.pageBg || a.backgroundColor}`);
    if (a.card) parts.push(`card: ${a.card}`);
    if (a.border) parts.push(`border: ${a.border}`);
    if (a.surface) parts.push(`surface: ${a.surface}`);

    return `🔤 Colors updated — ${parts.join(', ')}`;
  },

  reset_font_colors: () => '🔤 Font colors reset to default',

  audit_account: () => '📊 Account Health Audit',
  auto_organize_root: (a) => `🗂️ Auto-Organized ${a.count ? a.count + ' ' : ''}Root Bookmarks`,
  open_folder_tabs: (a) => `🚀 Launched "${cap(a.folderTitle || a.title || 'Folder')}" in Parallel Tabs`,
  audit_vault_security: () => '🔐 Vault Security Audit',
  export_vault: (a) => `💾 Exported Vault (${(a.format || 'HTML').toUpperCase()})`,
  get_account_stats: () => '📈 Account Telemetry Overview',
  apply_theme_preset: (a) => `🎨 Theme → ${cap(a.preset || a.theme || 'Preset')}`,
  set_view_density: (a) => `📐 Grid Density → ${cap(a.density || 'Standard')}`,
  save_all_tabs: (a) => `📚 Saved ${a.count || 'All'} Tabs`,
  extract_page_links: (a) => `🕸️ Extracted ${a.count || ''} Page Links`,
  search_library: (a) => `🔍 Library Search (${a.matchesCount || 0} matches)`,
  clean_duplicates: (a) => `🧹 Cleaned ${a.duplicateCount ? a.duplicateCount + ' ' : ''}Duplicate Bookmarks`,
  find_duplicates: (a) => `🔍 Detected ${a.duplicateCount || 0} Duplicate Bookmarks`,
  clean_empty_folders: (a) => `🗑️ Purged ${a.removedCount ? a.removedCount + ' ' : ''}Empty Folders`,
  purge_empty_folders: (a) => `🗑️ Purged ${a.removedCount ? a.removedCount + ' ' : ''}Empty Folders`,
  batch_tag: (a) => `🏷️ Batch Tagged ${a.updatedCount ? a.updatedCount + ' ' : ''}Bookmarks`,
  filter_view: (a) => `🎯 Filtered View (${a.tag ? '#' + a.tag : a.query || 'active'})`,
  reset_filter: () => '🔄 Reset View Filters',
  get_ai_capabilities: () => '👑 AI Capabilities Matrix',
  heal_broken_links: (a) => `🩹 Healed ${a.healedCount ? a.healedCount + ' ' : ''}Bookmark Links`,
  check_link_health: (a) => `🩺 Checked Health of ${a.totalChecked || 0} Links`,
  harmonize_tags: (a) => `✨ Harmonized ${a.updatedCount ? a.updatedCount + ' ' : ''}Bookmark Tags`,
  cluster_by_domain: (a) => `🌐 Clustered ${a.foldersCreated ? a.foldersCreated + ' ' : ''}Domain Folders`,
  sanitize_tracking_params: (a) => `🛡️ Sanitized ${a.sanitizedCount ? a.sanitizedCount + ' ' : ''}Tracking URLs`,
  archive_stale_bookmarks: (a) => `📦 Archived ${a.archivedCount ? a.archivedCount + ' ' : ''}Stale Bookmarks`,
  set_sort_mode: (a) => `🔀 Sort Order → ${cap(a.mode || 'Custom')}`,
  sleep_inactive_tabs: (a) => `💤 Suspended ${a.discardedCount ? a.discardedCount + ' ' : ''}Inactive Tabs`,
  export_extension_vault: (a) => `💾 Exported Extension Vault (${cap(a.format || 'json')})`,
  scrape_page_links: (a) => `🕸️ Scraped ${a.count || 0} Active Page Links`,
  wayback_restore: (a) => `🏛️ Wayback Machine Snapshot`,
  archive_snapshot: (a) => `🏛️ Wayback Archive Snapshot`,
  search_open_tabs: (a) => `🔎 Found Open Tabs`,
  clone_folder: (a) => `📑 Cloned Folder (${a.copiedCount || 0} items)`,
  duplicate_folder: (a) => `📑 Duplicated Folder`,
  summarize_vault_topics: () => `📊 Executive Knowledge Breakdown`,
  clean_duplicate_tabs: (a) => `🗂️ Cleaned Duplicate Tabs`,
  inspect_active_page: () => `🔍 Page Metadata Inspected`,
  extract_page_metadata: () => `🔍 Page Metadata Extracted`,
  merge_folders: (a) => `🔀 Merged Folders (${a.movedCount || 0} moved)`,
  export_selected_folder: (a) => `💾 Exported Folder (${cap(a.format || 'json')})`,
  ai_reading_queue: () => `📖 Curated Reading Queue`,
  clean_title_suffixes: (a) => `✨ Cleaned Titles (${a.cleanedCount || 0} cleaned)`,
  semantic_search_library: (a) => `🔍 Semantic Library Search (${a.matchesCount || 0} matches)`,
  auto_color_folders: (a) => `🎨 Colored ${a.coloredCount ? a.coloredCount + ' ' : ''}Folders`,
  tag_reading_durations: (a) => `⏱️ Tagged Reading Durations (${a.taggedCount || 0} links)`,
  audit_mixed_content: (a) => `🛡️ Audited Mixed Content (${a.insecureCount || 0} insecure)`,
  compare_library_snapshots: () => `📊 Library Snapshot Diff`,
  save_quote_to_bookmark: (a) => `📌 Saved Highlighted Quote`,
  bulk_untag: (a) => `🏷️ Bulk Untagged #${a.tag || 'tag'}`,
  find_lonely_bookmarks: (a) => `🏝️ Found ${a.lonelyCount || 0} Lonely Bookmarks`,
  unfurl_shortlinks: (a) => `🔗 Detected ${a.shortlinkCount || 0} Shortlinks`,
  get_tab_matrix: (a) => `🗂️ Browser Tab Matrix Overview`,
  generate_daily_briefing: () => `☀️ Executive Morning Briefing`,
  batch_tag_by_domain: (a) => `🏷️ Domain Auto-Tagged (${a.taggedCount || 0} links)`,
  dedupe_notes: () => `📝 Deduped Bookmark Notes`,
  export_markdown_table: () => `📋 Exported Markdown Table`,
  audit_stale_credentials: () => `🛡️ Credential Security Audit`,
  close_tab_by_pattern: (a) => `🗂️ Closed Pattern Tabs (${a.closedCount || 0})`,
  save_page_article: () => `📰 Saved Full Article Markdown`,
  flatten_deep_folders: () => `📂 Flattened Deep Folder Hierarchy`,
  bulk_rename_bookmarks: () => `✏️ Batch Renamed Bookmarks`,
  ai_diagnostics: () => `⚡ AI Sovereign Engine Diagnostics`,
};

export const ActionBadge = ({ action, tc, data, onOpenCredential }) => {
  const isCred = action.action === 'get_credentials';
  const isVaultAudit = action.action === 'audit_vault_security';
  const isOpen = action.action === 'open_bookmark';
  const isExport = action.action === 'export_vault';
  const isFolderTabs = action.action === 'open_folder_tabs';

  const bm = isCred
    ? findBookmarkByAnyRef(data, pickFirst(action.id, action.bookmarkId, action.bmId, action.title, action.name, action.query))
    : null;
  const hasCreds = Boolean(bm?.credentials?.trim() || action.credentials?.trim());
  const label = ACTION_LABELS[action.action]?.(action, data) || `✅ ${action.action}`;

  return (
    <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="flex flex-col items-center justify-center mb-2 gap-1.5">
      {isOpen && action.url ? (
        <a
          href={action.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3.5 py-1.5 rounded-full transition-all hover:scale-105 active:scale-95 shadow-sm cursor-pointer group"
          style={{
            backgroundColor: `${tc.accent}25`,
            color: tc.accent,
            border: `1px solid ${tc.accent}50`,
          }}
          title="Click to launch link in new tab"
        >
          <span>{label}</span>
          <span className="text-[11px] opacity-75 group-hover:translate-x-0.5 transition-transform">↗</span>
        </a>
      ) : isExport && action.downloadUrl ? (
        <a
          href={action.downloadUrl}
          download={action.filename || 'memark-vault-backup.html'}
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3.5 py-1.5 rounded-full transition-all hover:scale-105 active:scale-95 shadow-sm cursor-pointer group"
          style={{
            backgroundColor: 'rgba(16, 185, 129, 0.2)',
            color: '#34D399',
            border: '1px solid rgba(16, 185, 129, 0.5)',
          }}
          title="Download exported vault backup"
        >
          <span>{label}</span>
          <span className="text-[11px]">⬇️</span>
        </a>
      ) : (
        <span className="text-[10px] px-3 py-1 rounded-full font-medium" style={{ backgroundColor: `${tc.accent}25`, color: tc.accent, border: `1px solid ${tc.accent}40` }}>
          {label}
        </span>
      )}
      {isCred && hasCreds && (
        <button
          type="button"
          onClick={() => onOpenCredential?.(bm || action)}
          className="text-[11px] font-semibold px-3 py-1.5 rounded-xl border flex items-center gap-1.5 transition-all hover:scale-105 active:scale-95 shadow-md cursor-pointer"
          style={{
            backgroundColor: 'rgba(16, 185, 129, 0.2)',
            borderColor: 'rgba(16, 185, 129, 0.5)',
            color: '#34D399',
          }}
        >
          <span>🔐 Open in Secure Vault Popup</span>
        </button>
      )}
      {isVaultAudit && action.vaultAudit && (
        <div
          className="text-[10px] font-medium px-3 py-1 rounded-lg border flex items-center gap-2"
          style={{
            backgroundColor: 'rgba(245, 158, 11, 0.15)',
            borderColor: 'rgba(245, 158, 11, 0.4)',
            color: '#FBBF24',
          }}
        >
          <span>🛡️ {action.vaultAudit.total || 0} Vault Credentials Inspected</span>
        </div>
      )}
    </motion.div>
  );
};

export function StatsBar({ data, tc }) {
  const totalBm = Object.values(data).flatMap((f) => (f.items || []).filter((i) => i.type === 'link')).length;
  const totalFolders = Object.values(data).filter((f) => f.id !== 'root').length;
  const withNotes = Object.values(data).flatMap((f) => (f.items || []).filter((i) => i.type === 'link' && i.note?.trim())).length;
  const sharedCount = Object.values(data).filter((f) => f._isShared || f._isOwnerShared).length;

  return (
    <div className="flex items-center justify-around py-2 border-b text-[10px]" style={{ borderColor: tc.border }}>
      {[
        { icon: FiBookmark, label: 'Bookmarks', val: totalBm },
        { icon: FiFolder, label: 'Folders', val: totalFolders },
        { icon: FiSearch, label: 'With Notes', val: withNotes },
        { icon: FiShare2, label: 'Shared', val: sharedCount },
      ].map(({ icon, label, val }) => (
        <div key={label} className="flex flex-col items-center gap-0.5">
          <div className="flex items-center gap-1" style={{ color: tc.accent }}>
            <SafeIcon icon={icon} className="w-3 h-3" />
            <span className="font-bold text-xs">{val}</span>
          </div>
          <span style={{ color: tc.muted }}>{label}</span>
        </div>
      ))}
    </div>
  );
}